import React, { useContext } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Callout,
  Combobox,
  Box as Form,
  Box as FormActions,
  Box as FormFields,
  Input,
} from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactCard } from 'pages/Contacts/components/ContactCard'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { ExpirySelect } from '../components/TravelRuleExpirySelect/ExpirySelect'
import { usePaymentLinkForm } from './hooks/usePaymentLinkForm'
import { ExpirtyDateValues } from './schemas/paymentLinkSchema'

export const PaymentLinkDetails = () => {
  const { t } = useTranslation()
  const [walletAction] = useContext(WalletActionContext)
  const contact = useAtomValue(contactAtom)

  const {
    walletOptions,
    currenciesOptions,
    isFetchingWallets,
    isFetchingCurrencies,
    isCurrencyMismatch,
    errors,
    requestError,
    isPending,
    walletError,
    formFields,
    activeWallet,
    expiryDateOptions,
    handleExpiryValueChange,
    handleOpen,
    setSearchWalletQuery,
    watch,
    setValue,
    handleWalletOnChange,
    handleSubmit,
    onSubmit,
  } = usePaymentLinkForm()

  return (
    <Form
      tag="form"
      data-testid="payments-create-form"
      paddingT={24}
      onSubmit={handleSubmit(onSubmit)}
    >
      <ContactCard
        title={t('payments.paymentLinks.requestPaymentFrom')}
        showAvatar={false}
        name={contact?.name}
      />

      <FormFields width="full" flexCol gapY={16} paddingT={16}>
        {requestError?.show && <RequestErrorCallout requestError={requestError} />}

        <Combobox
          {...formFields.walletId}
          id="from-wallet"
          testId="from-wallet"
          items={walletOptions}
          label={t('receivingWallet')}
          loading={isFetchingWallets}
          loadingInline={isFetchingWallets}
          onChange={handleWalletOnChange}
          onInputChange={setSearchWalletQuery}
          errorText={
            errors?.walletId?.message || (walletError && t('wallets.send.sendFromWalletRequired'))
          }
          onOpen={handleOpen}
          modal
        />
        <Box flex gapX={16} width="full">
          <Box className="flex-1">
            <Combobox
              {...formFields.currency}
              id="currency"
              testId="currency"
              key={watch('currency')}
              label={t('displayCurrency')}
              loading={isFetchingCurrencies}
              loadingInline={isFetchingCurrencies}
              items={currenciesOptions}
              defaultValue={watch('currency')}
              onChange={value => {
                setValue('currency', value, { shouldValidate: true, shouldDirty: true })
              }}
              modal
            />
          </Box>
          <Box className="flex-1">
            <Input
              {...formFields.amount}
              onChange={e => {
                setValue('amount', e.target.value, { shouldValidate: true, shouldDirty: true })
              }}
              testId="payment-amount"
              rightSuffixText={watch('currency')}
            />
          </Box>
        </Box>

        {isCurrencyMismatch && (
          <Callout
            data-testid="display-currency-warning"
            variant="warning"
            GhostButton={
              <Button
                testId="reset-currency"
                variant="ghost"
                noPadding
                className="underline"
                onClick={() => {
                  setValue('currency', activeWallet?.currency?.code, {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }}
              >
                {t('ResetTheDisplayCurrency')}
              </Button>
            }
          >
            {t('displayCurrencyNotice')}
          </Callout>
        )}

        <Input
          {...formFields.reference}
          onChange={e =>
            setValue('reference', e.target.value, { shouldValidate: true, shouldDirty: true })
          }
          testId="reference"
          placeholderText={t('uniqueReference')}
          errorText={errors?.reference?.message}
          error={!!errors?.reference}
        />

        <ExpirySelect
          options={expiryDateOptions}
          onValueChange={(values: ExpirtyDateValues) => handleExpiryValueChange(values)}
          errorText={errors?.expiryMinutes?.message}
        />

        <Input
          {...formFields.returnUrl}
          onChange={e =>
            setValue('returnUrl', e.target.value, { shouldValidate: true, shouldDirty: true })
          }
          testId="return-url"
          placeholderText="https://..."
          helperText={t('payments.paymentLinks.returnUrlHelperText')}
          rightLabel={t('optionalLabel')}
        />

        <FormActions flex justify="between">
          <Button testId="cancel" variant="secondary" onClick={walletAction?.onClose}>
            {t('cancel')}
          </Button>

          <Button
            testId="create-request"
            type="submit"
            variant="primary"
            loading={isPending}
            disabled={!!Object.keys(errors)?.length}
          >
            {t('payments.paymentLinks.createRequest')}
          </Button>
        </FormActions>
      </FormFields>
    </Form>
  )
}
